import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Input,
  MenuProps,
  Modal,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
  message,
  Tooltip,
  Avatar,
  Divider,
} from 'antd';
import {
  InfoCircleOutlined,
  UserAddOutlined,
  CloseOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  MoreOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import PhoneInput from '@tools/components/PhoneInput';
import { isValidPhoneNumber } from 'react-phone-number-input';
import type { CountryCode, E164Number } from 'libphonenumber-js';

import { useAppDispatch, useAppSelector } from '@store/store';
import {
  createCustomer,
  deleteCustomer,
  fetchCustomer,
  searchCustomers,
  selectPosCustomers,
  updateCustomer,
  type Customer,
  type CustomerAddress,
  type CustomerCreatePayload,
  type CustomerPhoneConflictError,
  type CustomerRelationshipResolutionPayload,
  type CustomerSocialLinks,
} from '@store/slices/posCustomersSlice';
import { selectAppointmentsCustomerPhoneRequired } from '@store/slices/settingsSlice';

import {
  CustomerExtendedForm,
  type CustomerExtendedFormValue,     // add this
  type CustomerAddressDraft,
  type CustomerDocumentType,
  type CustomerExtendedFormErrors,
  type CustomerSecondaryPhoneDraft,
  type CustomerSocialDraft,
  validateSecondaryPhoneDraft,
} from './CustomerExtendedForm';
import { normalizeSocialInput, SocialNormalizationError, type SocialNetwork } from '@tools/socialLinks';
import type { PosCustomerBalanceSummary } from '@erp/pos/types.ts';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import { parseUserSearchQuery, searchUsers } from '@tools/search/userSearch.ts';

/**
 * Componente controlado. Mantiene la UX de CustomerPicker, pero ahora
 * soporta dos modos equivalentes a CustomerSection:
 *   - Por ID (value/onChange)
 *   - Por objeto (customer/onSelectCustomer/onClearCustomer)
 * También resuelve conflictos por teléfono permitiendo definir accesos
 * entre clientes relacionados desde el mismo modal.
 */
export interface CustomerPickerProps {
  /** Modo por ID (compatible con Form.Item name="customer_id") */
  value?: number | null;
  onChange?: (value: number | null) => void;
  /** Cliente reciente que se muestra primero como sugerencia, sin seleccionarlo. */
  recentCustomerId?: number | null;

  /** Modo por objeto (alineado con CustomerSection) */
  customer?: Customer | null;
  onSelectCustomer?: (customer: Customer | null) => void;
  onClearCustomer?: () => void;
  isHydrating?: boolean;       // por compatibilidad; no altera la UX aquí
  debounceMs?: number;         // por compatibilidad (la búsqueda ya es reactiva)

  /** Permiso para crear/editar/eliminar */
  canManage?: boolean;
  /** Botón de info del cliente seleccionado */
  onOpenInfo?: (customerId: number) => void;
  /** Accesibilidad y placeholder */
  ariaLabel?: string;
  placeholder?: string;
  /** Renderizar solo la accion que abre el modal de alta */
  createOnly?: boolean;
  /** Deshabilitar control completo */
  disabled?: boolean;

  /** Balance (si está disponible) — mismas claves que CustomerSection */
  balance?: PosCustomerBalanceSummary | null;

  /** Flag de carga del balance */
  balanceIsLoading?: boolean;
  /** Si llega, intenta abrir la edicion del cliente seleccionado */
  autoOpenEditCustomerId?: number | null;

  /** Estilo CSS opcional para personalizar el card del cliente seleccionado */
  selectedCardStyle?: React.CSSProperties;
  /** Clase CSS opcional para personalizar el card del cliente seleccionado */
  selectedCardClassName?: string;
  /** Alias de clase CSS opcional para la card del cliente seleccionado */
  selectedCardClass?: string;
}

// ――――――――――――――――――――――――――― Tipos y helpers locales ―――――――――――――――――――――――――――

export interface NewCustomerState {
  name: string;
  lastName: string;
  email: string;
  phone: E164Number | '';
  phoneCountry: CountryCode;
  documentType: CustomerDocumentType;
  documentNumber: string;
  avatarUrl: string;
  notes: string;
  social: CustomerSocialDraft;
  addresses: CustomerAddressDraft[];
  secondaryPhones: CustomerSecondaryPhoneDraft[];
}

const DEFAULT_CUSTOMER_COUNTRY: CountryCode = 'AR';

export const createEmptyNewCustomer = (): NewCustomerState => ({
  name: '',
  lastName: '',
  email: '',
  phone: '',
  phoneCountry: DEFAULT_CUSTOMER_COUNTRY,
  documentType: 'dni',
  documentNumber: '',
  avatarUrl: '',
  notes: '',
  social: { facebook: '', instagram: '', tiktok: '', youtube: '', whatsapp: '', website: '' },
  addresses: [],
  secondaryPhones: [],
});

const buildCustomerOption = (customer: Customer) => ({
  value: customer.id,
  label: `[L${customer.id}] ${customer.first_name} ${customer.last_name || ''}${customer.phone ? ` · ${customer.phone}` : ''}`,
});

const normalizeCountry = (value?: string | null): CountryCode => {
  if (!value) return DEFAULT_CUSTOMER_COUNTRY;
  const trimmed = value.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(trimmed) ? (trimmed as CountryCode) : DEFAULT_CUSTOMER_COUNTRY;
};

type RelationshipResolutionDraft = {
  existing_user_id: number;
  relationship_type_id?: number;
  is_binding_from_new_to_existing: boolean;
  is_binding_from_existing_to_new: boolean;
};

const RELATIONSHIP_ACCESS_HELP = {
  newToExisting:
    'Si activas este acceso, el cliente nuevo podrá operar y consultar la cuenta vinculada.',
  existingToNew:
    'Si activas este acceso, la cuenta vinculada podrá operar y consultar la cuenta del cliente que estás creando.',
} as const;

const isCustomerPhoneConflictError = (value: unknown): value is CustomerPhoneConflictError => {
  if (!value || typeof value !== 'object') return false;
  const code = (value as { code?: unknown }).code;
  return code === 'phone_conflict_requires_relationship';
};

const buildRelationshipResolutionDrafts = (
  conflict: CustomerPhoneConflictError,
): Record<number, RelationshipResolutionDraft> => {
  const defaultRelationshipTypeId = conflict.relationship_types[0]?.id;
  return conflict.matches.reduce<Record<number, RelationshipResolutionDraft>>((acc, match) => {
    acc[match.id] = {
      existing_user_id: match.id,
      relationship_type_id: defaultRelationshipTypeId,
      is_binding_from_new_to_existing: true,
      is_binding_from_existing_to_new: true,
    };
    return acc;
  }, {});
};

export const buildNewCustomerPayload = (
  customer: NewCustomerState,
  extras: {
    secondaryPhones?: CustomerSecondaryPhoneDraft[];
    addresses?: CustomerAddressDraft[];
    socialLinks?: Partial<CustomerSocialLinks>;
    relationshipsToExisting?: CustomerRelationshipResolutionPayload[];
  } = {},
) => {
  const trimmedName = customer.name.trim();
  const trimmedEmail = customer.email.trim();
  const normalizedPhone = customer.phone ? (customer.phone as string).trim() : '';
  const payload: CustomerCreatePayload = {
    first_name: trimmedName,
    email: trimmedEmail ? trimmedEmail : undefined,
    phone: normalizedPhone ? (normalizedPhone as E164Number) : undefined,
    phone_country: normalizedPhone ? customer.phoneCountry : undefined,
  };

  const lastName = customer.lastName.trim();
  if (lastName) payload.last_name = lastName;

  const doc = customer.documentNumber.trim();
  if (doc) {
    payload.document_number = doc;
    payload.document_type = customer.documentType;
  }

  const notes = customer.notes.trim();
  if (notes) payload.notes = notes;

  const avatar = customer.avatarUrl.trim();
  if (avatar) payload.avatar_url = avatar;

  if (extras.secondaryPhones) {
    const sanitized = extras.secondaryPhones
      .map((entry) => {
        const num = typeof entry.number === 'string' ? entry.number.trim() : '';
        if (!num) return null;
        return {
          id: entry.id,
          label: entry.label?.trim() || null,
          number: num,
          country_code: entry.country ?? customer.phoneCountry,
          is_primary: false,
        };
      })
      .filter(Boolean) as NonNullable<CustomerCreatePayload['phones']>;
    payload.phones = sanitized;
  }

  if (extras.addresses) {
    const sanitized = extras.addresses
      .map((address) => {
        const street = address.street.trim();
        if (!street) return null;
        return {
          id: address.id,
          label: address.label.trim() || null,
          street,
          city: address.city.trim() || null,
          state: address.state.trim() || null,
          postcode: address.postcode.trim() || null,
          country: address.country.trim() || null,
          notes: address.notes.trim() || null,
          phone_number: address.phoneNumber.trim() || null,
        } satisfies CustomerAddress;
      })
      .filter(Boolean) as CustomerAddress[];
    payload.addresses = sanitized;
  }

  if (extras.socialLinks && Object.keys(extras.socialLinks).length > 0) {
    payload.social_links = extras.socialLinks;
  }

  if (extras.relationshipsToExisting && extras.relationshipsToExisting.length > 0) {
    payload.relationships_to_existing = extras.relationshipsToExisting;
  }

  return payload;
};

// —— Balance: misma regla que CustomerSection (mostrar un único resumen)
const summarizeBalance = (balance?: PosCustomerBalanceSummary | null) => {
  if (!balance) return null;
  const credit = Number(balance.available_credit ?? 0) || 0;
  const debt = Number(balance.outstanding_debt ?? 0) || 0;
  if (debt > 0) return { type: 'debt' as const, amount: debt, label: 'Deuda' };
  if (credit > 0) return { type: 'credit' as const, amount: credit, label: 'A favor' };
  return null;
};


// ――――――――――――――――――――――――――― Componente principal ―――――――――――――――――――――――――――

export const CustomerPicker: React.FC<CustomerPickerProps> = ({
  // Modo por ID
  value,
  onChange,
  recentCustomerId,
  // Modo por objeto (CustomerSection-like)
  customer,
  onSelectCustomer,
  onClearCustomer,
  // isHydrating,
  // debounceMs,
  // Extras / UX
  canManage = true,
  onOpenInfo,
  ariaLabel = 'Cliente',
  placeholder = 'Buscar o crear cliente…',
  createOnly = false,
  disabled,
  balance,
  balanceIsLoading = false,
  autoOpenEditCustomerId,
  selectedCardStyle,
  selectedCardClassName,
  selectedCardClass,
}) => {
  const dispatch = useAppDispatch();
  const customers = useAppSelector(selectPosCustomers);
  const isPhoneRequired = useAppSelector(selectAppointmentsCustomerPhoneRequired);

  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<'basic' | 'advanced'>('basic');
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingCustomerId, setEditingCustomerId] = useState<number | null>(null);
  const [editingSnapshot, setEditingSnapshot] = useState<Customer | null>(null);
  const [newCustomer, setNewCustomer] = useState<NewCustomerState>(() => createEmptyNewCustomer());
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [extendedErrors, setExtendedErrors] = useState<CustomerExtendedFormErrors | null>(null);
  const [relationshipConflict, setRelationshipConflict] = useState<CustomerPhoneConflictError | null>(null);
  const [relationshipResolutions, setRelationshipResolutions] = useState<Record<number, RelationshipResolutionDraft>>({});
  const [pendingPhoneConflictChoice, setPendingPhoneConflictChoice] = useState<CustomerPhoneConflictError | null>(null);
  const autoOpenEditHandledRef = useRef<number | null>(null);

  /** Modo de control: por ID si `value` está definido; si no, por objeto con estado interno */
  const isIdControlled = value !== undefined;

  /** ID interno cuando estamos en modo por objeto; parte de `customer?.id` y luego se maneja solo */
  const [localId, setLocalId] = useState<number | null>(customer?.id ?? null);

  /** Mantener `localId` sincronizado solo cuando cambia el id del prop `customer` (y no estamos controlados por ID) */
  const customerId = customer?.id ?? null;
  useEffect(() => {
    if (!isIdControlled) {
      setLocalId(prev => (customerId !== prev ? customerId : prev));
    }
  }, [isIdControlled, customerId]);

  useEffect(() => {
    if (recentCustomerId == null || customers.some((item) => item.id === recentCustomerId)) {
      return;
    }
    void dispatch(fetchCustomer(recentCustomerId));
  }, [customers, dispatch, recentCustomerId]);

  // Debounce timer ref
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 🔁 Buscar clientes al teclear (con debounce de 300ms)
  useEffect(() => {
    // Clear previous timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    const mode = parseUserSearchQuery(search);

    // Función que ejecuta la búsqueda
    const executeSearch = () => {
      // Búsqueda por #ID exacto — solo necesita 1+ dígito
      if (mode.mode === 'exact_id') {
        void dispatch(searchCustomers({ q: mode.value, exact_id: true }));
        return;
      }

      // Búsqueda por #ID* (prefijo) — solo necesita 1+ dígito
      if (mode.mode === 'prefix_id') {
        void dispatch(searchCustomers({ q: mode.value, id_prefix: true }));
        return;
      }

      // Búsqueda normal — mínimo 3 caracteres
      const normalizedTerm = mode.mode === 'normal' ? mode.tokens.join(' ') : '';
      if (normalizedTerm.length >= 3) {
        void dispatch(searchCustomers(normalizedTerm));
      }
    };

    // Para búsquedas por ID (#), ejecutar con menos delay (150ms)
    // Para búsquedas normales, usar delay estándar (300ms)
    const delay = (mode.mode === 'exact_id' || mode.mode === 'prefix_id') ? 150 : 300;

    searchTimerRef.current = setTimeout(executeSearch, delay);

    // Cleanup on unmount or search change
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [search, dispatch]);

  // Opciones del select
  const options = useMemo(() => {
    const rankedCustomers = searchUsers(customers, {
      query: search,
      fields: [
        { key: 'id', kind: 'id', getValue: (item) => item.id },
        { key: 'first_name', getValue: (item) => item.first_name },
        { key: 'last_name', getValue: (item) => item.last_name },
        { key: 'phone', kind: 'phone', getValue: (item) => item.phone },
        { key: 'email', kind: 'email', getValue: (item) => item.email },
      ],
    });
    if (search.trim()) {
      return rankedCustomers.map(({ item }) => item).map(buildCustomerOption);
    }

    const recentCustomer = recentCustomerId == null
      ? null
      : customers.find((item) => item.id === recentCustomerId);
    const suggestedCustomers = recentCustomer
      ? [recentCustomer, ...customers.filter((item) => item.id !== recentCustomer.id)]
      : customers;
    return suggestedCustomers.map(buildCustomerOption);
  }, [customers, recentCustomerId, search]);

  // 🔗 Fuente única: si llega value (ID) lo priorizamos; si no, usamos customer?.id
  const derivedId = isIdControlled ? (value ?? null) : localId;

  // Cliente seleccionado efectivo (desde store por id, o customer prop si no está en store)
  const selected = useMemo(() => {
    if (derivedId == null) return undefined;
    // Preferimos el store; si no está, usamos el `customer` prop solo si coincide el id
    return (
      customers.find(c => c.id === derivedId) ??
      (customer?.id === derivedId ? customer : undefined)
    );
  }, [derivedId, customer, customers]);

  const hasSelection = !!selected;

  // ―――――――――――― Helpers de selección/notificación (ID y objeto) ――――――――――――
  const notifyClear = useCallback(() => {
    // 1) null para integraciones que normalizan a null
    onChange?.(null);
    // 2) undefined para integraciones que consideran "unset"
    (onChange as unknown as ((v: number | null | undefined) => void) | undefined)?.(undefined);
    onSelectCustomer?.(null);
    onClearCustomer?.();
    setSearch(''); // clean select text
  }, [onChange, onSelectCustomer, onClearCustomer]);

  const notifyAssign = useCallback(
    async (id: number) => {
      onChange?.(id);
      if (!isIdControlled) setLocalId(id);   // ← fija el id interno en modo por objeto
      setSearch(''); // clean select text when choosing

      // Intentamos obtener el objeto para notificar como CustomerSection
      let obj = customers.find((c) => c.id === id);
      if (!obj) {
        try {
          obj = await dispatch(fetchCustomer(id)).unwrap();
        } catch {
          obj = undefined;
        }
      }
      if (obj) onSelectCustomer?.(obj);
    },
    [onChange, onSelectCustomer, customers, dispatch, isIdControlled],
  );

  // ✔ Empareja el shape que pide CustomerExtendedFormValue
  const toExtendedDraft = (s: NewCustomerState): CustomerExtendedFormValue => ({
    // Campos "básicos" que tu ExtendedForm también espera
    lastName: s.lastName,

    // Campos extendidos
    documentType: s.documentType,
    documentNumber: s.documentNumber,
    avatarUrl: s.avatarUrl,
    notes: s.notes,
    social: s.social,
    addresses: s.addresses,
    secondaryPhones: s.secondaryPhones,
  });

  // ✔ Envoltura para onChange del ExtendedForm
  const setExtendedFromForm: React.Dispatch<React.SetStateAction<CustomerExtendedFormValue>> = (updater) => {
    setNewCustomer((prev) => {
      const base = toExtendedDraft(prev);
      const next =
        typeof updater === 'function'
          ? (updater as (v: CustomerExtendedFormValue) => CustomerExtendedFormValue)(base)
          : updater;

      return {
        ...prev,

        // Sincronizamos también lo "básico" si tu ExtendedForm lo edita
        lastName: next.lastName,

        // Y lo extendido
        documentType: next.documentType,
        documentNumber: next.documentNumber,
        avatarUrl: next.avatarUrl,
        notes: next.notes,
        social: next.social,
        addresses: next.addresses,
        secondaryPhones: next.secondaryPhones,
      };
    });
  };

  // ――――――――――――― Menú contextual del cliente ―――――――――――――
  const customerMenu: MenuProps = {
    items: [
      {
        key: 'edit',
        label: 'Editar cliente',
        icon: <EditOutlined />,
        disabled: !selected,
      },
      { type: 'divider' },
      {
        key: 'delete',
        label: 'Eliminar cliente',
        icon: <DeleteOutlined />,
        danger: true,
        disabled: !selected,
      },
    ],
    onClick: ({ key }) => {
      if (key === 'edit') {
        void openEdit();
      } else if (key === 'delete') {
        Modal.confirm({
          title: '¿Eliminar cliente?',
          content: `Se eliminará ${selected?.first_name} ${selected?.last_name || ''} de forma permanente.`,
          okText: 'Sí, eliminar',
          cancelText: 'Cancelar',
          okButtonProps: { danger: true },
          onOk: handleDelete,
        });
      }
    },
  };

  const resetDraft = () => {
    setNewCustomer(createEmptyNewCustomer());
    setPhoneError(null);
    setExtendedErrors(null);
    setRelationshipConflict(null);
    setRelationshipResolutions({});
    setPendingPhoneConflictChoice(null);
    setModalTab('basic');
    setMode('create');
    setEditingCustomerId(null);
    setEditingSnapshot(null);
  };

  const openCreate = () => {
    resetDraft();
    setMode('create');
    setModalOpen(true);
    setSearch(''); // limpiar búsqueda al abrir modal de alta
  };

  const populateFrom = (customer: Customer) => {
    const primaryCountry = normalizeCountry(customer.phone_country ?? undefined);
    const sanitizeDocType = (value?: string | null): CustomerDocumentType =>
      value === 'cuit' || value === 'passport' || value === 'other' ? value : 'dni';

    const primaryPhone = (customer.phone ?? '') as E164Number | '';
    const existingPhones = customer.phones ?? [];
    const secondaryPhones: CustomerSecondaryPhoneDraft[] = existingPhones
      .filter((entry) => {
        const normalized = entry.number?.trim();
        if (!normalized) return false;
        return !(primaryPhone && normalized === primaryPhone);
      })
      .map((entry) => ({
        id: entry.id,
        label: entry.label ?? '',
        number: (entry.number ?? '') as E164Number | '',
        country: normalizeCountry(entry.country_code ?? undefined),
      }));

    const addresses: CustomerAddressDraft[] = (customer.addresses ?? []).map((a) => ({
      id: a.id,
      label: a.label ?? '',
      street: a.street ?? '',
      city: a.city ?? '',
      state: a.state ?? '',
      postcode: a.postcode ?? '',
      country: a.country ?? '',
      notes: a.notes ?? '',
      phoneNumber: a.phone_number ?? '',
    }));

    const social = customer.social_links ?? ({} as CustomerSocialLinks | null);

    setNewCustomer({
      name: customer.first_name ?? '',
      lastName: customer.last_name ?? '',
      email: customer.email ?? '',
      phone: primaryPhone,
      phoneCountry: primaryCountry,
      documentType: sanitizeDocType(customer.document_type ?? undefined),
      documentNumber: customer.document_number ?? '',
      avatarUrl: customer.avatar_url ?? '',
      notes: customer.notes ?? '',
      social: {
        facebook: social?.facebook ?? '',
        instagram: social?.instagram ?? '',
        tiktok: social?.tiktok ?? '',
        youtube: social?.youtube ?? '',
        whatsapp: social?.whatsapp ?? '',
        website: social?.website ?? '',
      },
      addresses,
      secondaryPhones,
    });
    setEditingSnapshot(customer);
    setPhoneError(null);
    setExtendedErrors(null);
    setRelationshipConflict(null);
    setRelationshipResolutions({});
    setPendingPhoneConflictChoice(null);
    setModalTab('basic');
  };

  const openEdit = async () => {
    const id = selected?.id ?? derivedId;
    if (!id) return;
    let target: Customer | undefined;
    try {
      target = await dispatch(fetchCustomer(id)).unwrap();
    } catch {
      target = selected;
    }
    if (!target) {
      message.error('No se encontró información del cliente');
      return;
    }
    populateFrom(target);
    setMode('edit');
    setEditingCustomerId(target.id);
    setModalOpen(true);
  };

  useEffect(() => {
    if (!autoOpenEditCustomerId) {
      autoOpenEditHandledRef.current = null;
      return;
    }
    if (!canManage || disabled) return;

    const currentSelectedId = selected?.id ?? derivedId;
    if (currentSelectedId !== autoOpenEditCustomerId) return;
    if (autoOpenEditHandledRef.current === autoOpenEditCustomerId) return;

    autoOpenEditHandledRef.current = autoOpenEditCustomerId;
    void openEdit();
  }, [autoOpenEditCustomerId, canManage, derivedId, disabled, openEdit, selected]);

  const handleUseExistingConflictMatch = useCallback(
    async (matchId: number) => {
      await notifyAssign(matchId);
      setPendingPhoneConflictChoice(null);
      setModalOpen(false);
      resetDraft();
      setSearch('');
    },
    [notifyAssign],
  );

  const handleAssociateNewFromConflict = useCallback(() => {
    if (!pendingPhoneConflictChoice) return;
    setRelationshipConflict(pendingPhoneConflictChoice);
    setRelationshipResolutions(buildRelationshipResolutionDrafts(pendingPhoneConflictChoice));
    setPendingPhoneConflictChoice(null);
    setPhoneError(null);
    setModalTab('basic');
  }, [pendingPhoneConflictChoice]);

  const handleDelete = async () => {
    const id = selected?.id ?? derivedId;
    if (!id) return;
    try {
      await dispatch(deleteCustomer(id)).unwrap();
      notifyClear();
      message.success('Cliente eliminado correctamente');
    } catch {
      message.error('No se pudo eliminar el cliente');
    }
  };

  const handlePersist = async () => {
    if (!canManage) {
      message.error('No tenés permisos para gestionar clientes');
      return;
    }

    const trimmedName = newCustomer.name.trim();
    if (!trimmedName) {
      message.error('El nombre es obligatorio');
      return;
    }

    const normalizedPhone = newCustomer.phone ? (newCustomer.phone as string).trim() : '';

    if (isPhoneRequired && !normalizedPhone) {
      setPhoneError('El teléfono es obligatorio');
      setModalTab('basic');
      return;
    }

    if (normalizedPhone) {
      if (!isValidPhoneNumber(normalizedPhone as E164Number)) {
        setPhoneError('Ingresá un teléfono válido');
        setModalTab('basic');
        return;
      }
    }

    setPhoneError(null);

    // Validaciones extendidas (sin cambios)
    const prev = editingSnapshot;
    const extendedErrors: CustomerExtendedFormErrors = {};
    let hasExtendedErrors = false;

    const doc = newCustomer.documentNumber.trim();
    if (doc && !/^[A-Za-z0-9.-]{6,32}$/.test(doc)) {
      extendedErrors.documentNumber = 'Ingresá un documento válido (6-32 caracteres)';
      hasExtendedErrors = true;
    }

    const secErrors: Array<{ number?: string | null } | null> = [];
    const sanitizedSecondary: CustomerSecondaryPhoneDraft[] = [];
    newCustomer.secondaryPhones.forEach((p, i) => {
      const num = p.number ? (p.number as string).trim() : '';
      const lbl = p.label.trim();
      if (!num && !lbl) {
        secErrors[i] = null;
        return;
      }
      if (!num) {
        secErrors[i] = { number: 'Ingresá un teléfono' };
        hasExtendedErrors = true;
        return;
      }
      const msg = validateSecondaryPhoneDraft({ ...p, number: num as E164Number | '' });
      if (msg) {
        secErrors[i] = { number: msg };
        hasExtendedErrors = true;
        return;
      }
      sanitizedSecondary.push({ ...p, label: lbl, number: num as E164Number | '' });
      secErrors[i] = null;
    });
    if (secErrors.some(Boolean)) extendedErrors.secondaryPhones = secErrors;

    const addrErrors: Array<{ street?: string | null; city?: string | null } | null> = [];
    const sanitizedAddresses: CustomerAddressDraft[] = [];
    newCustomer.addresses.forEach((a, i) => {
      const t = {
        ...a,
        label: a.label.trim(),
        street: a.street.trim(),
        city: a.city.trim(),
        state: a.state.trim(),
        postcode: a.postcode.trim(),
        country: a.country.trim(),
        notes: a.notes.trim(),
        phoneNumber: a.phoneNumber.trim(),
      } satisfies CustomerAddressDraft;

      const hasContent = [t.label, t.street, t.city, t.state, t.postcode, t.country, t.notes, t.phoneNumber].some(Boolean);
      if (!hasContent) {
        addrErrors[i] = null;
        return;
      }
      const err: { street?: string | null; city?: string | null } = {};
      if (!t.street) err.street = 'Indicá la calle y número';
      if (!t.city) err.city = 'Indicá la ciudad';
      if (Object.keys(err).length) {
        addrErrors[i] = err;
        hasExtendedErrors = true;
        return;
      }
      addrErrors[i] = null;
      sanitizedAddresses.push(t);
    });
    if (addrErrors.some(Boolean)) extendedErrors.addresses = addrErrors;

    const normalizedSocial: Partial<CustomerSocialLinks> = {};
    const socialErrs: NonNullable<CustomerExtendedFormErrors['social']> = {};
    const socialNetworks: Array<Exclude<SocialNetwork, 'twitter'>> = ['instagram', 'facebook', 'tiktok', 'youtube'];
    socialNetworks.forEach((net) => {
      const raw = newCustomer.social[net]?.trim?.() ?? '';
      if (!raw) {
        if (mode === 'edit' && prev?.social_links && prev.social_links[net]) normalizedSocial[net] = null;
        return;
      }
      try {
        const norm = normalizeSocialInput(net, raw);
        normalizedSocial[net] = norm.url;
      } catch (error) {
        hasExtendedErrors = true;
        if (error instanceof SocialNormalizationError) {
          if (error.code === 'empty_handle') socialErrs[net] = 'Completá el usuario de la red';
          else if (error.code === 'invalid_domain') socialErrs[net] = 'Usá un enlace del dominio oficial';
          else socialErrs[net] = 'Link inválido';
        } else socialErrs[net] = 'Link inválido';
      }
    });

    (['whatsapp', 'website'] as const).forEach((key) => {
      const raw = newCustomer.social[key].trim();
      if (!raw) {
        if (mode === 'edit' && prev?.social_links && prev.social_links[key]) normalizedSocial[key] = null;
        return;
      }
      try {
        const u = new URL(raw, raw.startsWith('http') ? undefined : 'https://');
        if (u.protocol !== 'http:' && u.protocol !== 'https:') throw new Error('invalid_protocol');
        normalizedSocial[key] = u.toString();
      } catch {
        hasExtendedErrors = true;
        (socialErrs as any)[key] = 'Ingresá una URL válida (http/https)';
      }
    });

    if (Object.keys(socialErrs).length > 0) extendedErrors.social = socialErrs;

    if (hasExtendedErrors) {
      setExtendedErrors(extendedErrors);
      setModalTab('advanced');
      return;
    }

    setExtendedErrors(null);

    const extras: {
      secondaryPhones?: CustomerSecondaryPhoneDraft[];
      addresses?: CustomerAddressDraft[];
      socialLinks?: Partial<CustomerSocialLinks>;
      relationshipsToExisting?: CustomerRelationshipResolutionPayload[];
    } = {};
    if (sanitizedSecondary.length) extras.secondaryPhones = sanitizedSecondary;
    if (sanitizedAddresses.length) extras.addresses = sanitizedAddresses;
    if (Object.keys(normalizedSocial).length) extras.socialLinks = normalizedSocial;

    if (relationshipConflict) {
      const pendingResolutions = relationshipConflict.matches.map((match) => {
        const current = relationshipResolutions[match.id] ?? {
          existing_user_id: match.id,
          relationship_type_id: undefined,
          is_binding_from_new_to_existing: false,
          is_binding_from_existing_to_new: false,
        };
        return current;
      });
      const missingRelationship = pendingResolutions.find((entry) => !entry.relationship_type_id);
      if (missingRelationship) {
        message.error('Seleccioná una relación para cada perfil existente.');
        setModalTab('basic');
        return;
      }
      extras.relationshipsToExisting = pendingResolutions.map((entry) => ({
        existing_user_id: entry.existing_user_id,
        relationship_type_id: Number(entry.relationship_type_id),
        is_binding_from_new_to_existing: entry.is_binding_from_new_to_existing,
        is_binding_from_existing_to_new: entry.is_binding_from_existing_to_new,
      }));
    }

    try {
      if (mode === 'edit' && editingCustomerId) {
        // Limpiezas “a vacío” si corresponde
        const prev = editingSnapshot;
        if ((prev?.phones?.length ?? 0) > 0 && sanitizedSecondary.length === 0 && newCustomer.secondaryPhones.length === 0) {
          extras.secondaryPhones = [];
        }
        if ((prev?.addresses?.length ?? 0) > 0 && sanitizedAddresses.length === 0 && newCustomer.addresses.length === 0) {
          extras.addresses = [];
        }
        if (prev?.social_links && Object.values(prev.social_links).some(Boolean) && Object.keys(normalizedSocial).length === 0) {
          extras.socialLinks = {};
        }
        const payload = buildNewCustomerPayload(newCustomer, extras);
        const updated = await dispatch(updateCustomer({ id: editingCustomerId, payload })).unwrap();
        message.success('Cliente actualizado');
        onChange?.(updated.id);
        onSelectCustomer?.(updated); // ← notifica objeto
      } else {
        const payload = buildNewCustomerPayload(newCustomer, extras);
        const created = await dispatch(createCustomer(payload)).unwrap();
        message.success('Cliente creado');
        onChange?.(created.id);
        onSelectCustomer?.(created); // ← notifica objeto
      }
      setModalOpen(false);
      resetDraft();
      setSearch(''); // limpieza extra por si se vuelve a abrir el select
    } catch (error) {
      if (isCustomerPhoneConflictError(error)) {
        setPhoneError(error.message);
        if (mode === 'create' && !relationshipConflict) {
          setPendingPhoneConflictChoice(error);
        } else {
          setRelationshipConflict(error);
          setRelationshipResolutions(buildRelationshipResolutionDrafts(error));
          setModalTab('basic');
        }
        message.warning(error.message);
        return;
      }
      message.error('No se pudo guardar el cliente');
    }
  };

  /** Renderiza resumen de balance (único chip) si hay datos, o “Calculando…” */
  const renderBalanceSummary = () => {
    if (balanceIsLoading) {
      return (
        <Tag color="processing" style={{ marginTop: 6 }} className="ms-5">
          <LoadingOutlined spin style={{ marginRight: 6 }} />
          Calculando saldo…
        </Tag>
      );
    }
    const summary = summarizeBalance(balance);
    if (!summary) return null;

    const currency = balance?.currency || 'ARS';
    const color = summary.type === 'debt' ? 'error' : 'success';
    return (
      <Tag color={color} style={{ marginTop: 6 }} className="ms-5">
        {summary.label}: {getFormattedPrice(summary.amount, currency)}
      </Tag>
    );
  };

  return (
    <div style={{ width: '100%' }}>
      {createOnly ? (
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={openCreate}
          disabled={disabled || !canManage}
        >
          Crear cliente
        </Button>
      ) : !hasSelection ? (
        <Space orientation="vertical" style={{ width: '100%' }} size="small">
          <Space.Compact block style={{ width: '100%' }}>
            <Select
              showSearch
              allowClear
              disabled={disabled}
              placeholder={placeholder}
              aria-label={ariaLabel}
              filterOption={(input, option) => {
                // Para búsquedas por ID (#N o #N*), confiar solo en resultados del backend
                // El filterOption recibe el input literal (ej: "#98") pero las labels son "[L98] ..."
                const parsed = parseUserSearchQuery(input);
                const rawLabel = option?.label ?? '';
                const rawValue = option?.value ?? '';
                return searchUsers(
                  [{ label: rawLabel, value: rawValue }],
                  {
                    query: input,
                    fields: [
                      { key: 'label', getValue: (item) => item.label },
                      { key: 'id', kind: 'id', getValue: (item) => item.value },
                    ],
                    minQueryLength: parsed.mode === 'normal' ? 1 : 0,
                  },
                ).length > 0;
                // Para búsqueda normal, filtrar localmente
              }}
              options={options}
              value={undefined}                 // nunca “fija” una selección; se limpia tras elegir
              searchValue={search}              // controlamos el término para “clean select”
              onSearch={setSearch}
              onChange={(val) => {
                const id = typeof val === 'number' ? val : null;
                if (id == null) {
                  notifyClear();
                } else {
                  void notifyAssign(id);
                }
                setSearch(''); // limpieza al cambiar (selección o clear)
              }}
              onOpenChange={(open) => {
                if (!open) setSearch(''); // al cerrar, limpiar el término
              }}
              style={{ width: '100%' }}
              suffixIcon={<SearchOutlined />}
              popupRender={(menu) => (
                <div>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ padding: '8px' }}>
                    <Button
                      type="primary"
                      block
                      icon={<UserAddOutlined />}
                      onClick={openCreate}
                      disabled={!canManage}
                    >
                      Crear nuevo cliente
                    </Button>
                  </div>
                </div>
              )}
            />
            <Tooltip title={canManage ? 'Crear nuevo cliente' : 'Sin permisos'}>
              <Button icon={<UserAddOutlined />} onClick={openCreate} disabled={disabled || !canManage} type="default" />
            </Tooltip>
          </Space.Compact>
          <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
            <InfoCircleOutlined style={{ marginRight: 4 }} />
            Escribí 3+ caracteres para buscar
          </Typography.Text>
        </Space>
      ) : (
        <div
          className={selectedCardClassName || selectedCardClass}
          style={{
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            ...selectedCardStyle,
          }}
        >
          <Space orientation="vertical" style={{ width: '100%' }} size="small">
            <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space align="start" wrap={true} style={{ width: '100%' }}>
                <Space align="start">
                  <Avatar
                    size={40}
                    src={selected?.avatar_url}
                    icon={!selected?.avatar_url && <UserOutlined />}
                    style={{
                      backgroundColor: selected?.avatar_url ? undefined : 'var(--surface-border)',
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <Typography.Text strong style={{ display: 'block', fontSize: '15px', width: '100%', textAlign: 'left' }}>
                      [{selected?.id}] {selected?.first_name} {selected?.last_name}
                    </Typography.Text>
                    <Space orientation={'horizontal'} wrap={true}>
                      {selected?.phone && (
                        <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <PhoneOutlined style={{ fontSize: '12px' }} />
                          {selected.phone}
                        </Typography.Text>
                      )}
                      {selected?.email && (
                        <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MailOutlined style={{ fontSize: '12px' }} />
                          {selected.email}
                        </Typography.Text>
                      )}
                    </Space>
                  </div>
                </Space>

                {/* Balance (resumen único) */}
                {renderBalanceSummary()}
              </Space>

              <Space.Compact>
                {onOpenInfo && selected?.id && (
                  <Tooltip title="Ver información">
                    <Button size="small" icon={<InfoCircleOutlined />} onClick={() => onOpenInfo?.(selected.id)} type="text" />
                  </Tooltip>
                )}
                {canManage && (
                  <Dropdown menu={customerMenu} trigger={['click']}>
                    <Tooltip title="Más opciones">
                      <Button size="small" icon={<MoreOutlined />} type="text" />
                    </Tooltip>
                  </Dropdown>
                )}
                <Tooltip title="Quitar cliente">
                  <Button size="small" icon={<CloseOutlined />} onClick={notifyClear} type="text" danger />
                </Tooltip>
              </Space.Compact>
            </Space>
          </Space>
        </div>
      )}

      <Modal
        open={modalOpen}
        onCancel={() => { setModalOpen(false); resetDraft(); }}
        onOk={handlePersist}
        okText={mode === 'edit' ? 'Guardar cambios' : 'Crear cliente'}
        cancelText="Cancelar"
        title={
          <Space>
            {mode === 'edit' ? <EditOutlined /> : <UserAddOutlined />}
            <span>{mode === 'edit' ? 'Editar cliente' : 'Nuevo cliente'}</span>
          </Space>
        }
        width={520}
        destroyOnHidden
        styles={{
          body: { paddingTop: 20 },
        }}
      >
        <Tabs
          activeKey={modalTab}
          onChange={(k) => setModalTab(k as any)}
          items={[
            {
              key: 'basic',
              label: 'Básico',
              children: (
                <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                  <Form.Item label="Nombre" required style={{ marginBottom: 0 }} htmlFor="customerPickerName">
                    <Input
                      id="customerPickerName"
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer((s) => ({ ...s, name: e.target.value }))}
                      placeholder="Ej: Juan"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item label="Apellido" style={{ marginBottom: 0 }} htmlFor="customerPickerLastName">
                    <Input
                      id="customerPickerLastName"
                      value={newCustomer.lastName}
                      onChange={(e) => setNewCustomer((s) => ({ ...s, lastName: e.target.value }))}
                      placeholder="Ej: Pérez"
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item label="Email" style={{ marginBottom: 0 }} htmlFor="customerPickerEmail">
                    <Input
                      id="customerPickerEmail"
                      type="email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer((s) => ({ ...s, email: e.target.value }))}
                      placeholder="ejemplo@email.com"
                      prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <Space>
                        <span>Teléfono</span>
                        {isPhoneRequired && (
                          <Tag color="error" style={{ margin: 0 }}>
                            Requerido
                          </Tag>
                        )}
                      </Space>
                    }
                    validateStatus={phoneError ? 'error' : undefined}
                    help={phoneError ?? undefined}
                    style={{ marginBottom: 0 }}
                    htmlFor="customerPickerPhone"
                  >
                    <PhoneInput
                      id="customerPickerPhone"
                      defaultCountry={newCustomer.phoneCountry}
                      value={newCustomer.phone as E164Number | ''}
                      onChange={(val) => {
                        setNewCustomer((s) => ({ ...s, phone: (val ?? '') as E164Number | '' }));
                        if (phoneError) setPhoneError(null);
                        if (relationshipConflict) {
                          setRelationshipConflict(null);
                          setRelationshipResolutions({});
                        }
                        if (pendingPhoneConflictChoice) setPendingPhoneConflictChoice(null);
                      }}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>

                  {relationshipConflict && (
                      <Space orientation="vertical" size="middle" style={{ width: '100%', marginBottom: 15 }}>
                        {relationshipConflict.matches.map((match) => {
                          const draft = relationshipResolutions[match.id] ?? {
                            existing_user_id: match.id,
                            relationship_type_id: relationshipConflict.relationship_types[0]?.id,
                            is_binding_from_new_to_existing: false,
                            is_binding_from_existing_to_new: false,
                          };
                          const label = [match.first_name, match.last_name].filter(Boolean).join(' ') || `Cliente ${match.id}`;
                          return (
                            <div
                              key={match.id}
                              style={{
                                borderTop:
                                  '1px solid var(--ant-color-border-secondary, rgba(5, 5, 5, 0.06))',
                                paddingTop: 12,
                              }}
                            >
                              <Space
                                direction="horizontal"
                                style={{ width: '100%' }}
                                styles={{item: {flexGrow: 1}}}
                              >
                                <div>
                                  <Typography.Text
                                    strong
                                    style={{ marginRight: 15 }}
                                  >
                                    Relación con {label}
                                  </Typography.Text>
                                  <Typography.Text
                                    type="secondary"
                                    style={{ display: 'block' }}
                                  >
                                    [
                                    {match.phone ||
                                      match.email ||
                                      `#${match.id}`}
                                    ]
                                  </Typography.Text>
                                </div>
                                <Select
                                  value={draft.relationship_type_id}
                                  options={relationshipConflict.relationship_types.map(
                                    (item) => ({
                                      value: item.id,
                                      label: item.label,
                                    })
                                  )}
                                  placeholder="Seleccioná la relación"
                                  onChange={(value) => {
                                    setRelationshipResolutions((prev) => ({
                                      ...prev,
                                      [match.id]: {
                                        ...(prev[match.id] ?? draft),
                                        existing_user_id: match.id,
                                        relationship_type_id: value,
                                      },
                                    }));
                                    if (phoneError) setPhoneError(null);
                                  }}
                                  className={'w-100'}
                                />
                              </Space>
                              <Space orientation="vertical" size="small" style={{ width: '100%', marginTop: 12 }}>
                                <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                                  <Space size={6}>
                                    <Typography.Text>Accederá a {label}</Typography.Text>
                                    <Tooltip title={RELATIONSHIP_ACCESS_HELP.newToExisting}>
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<InfoCircleOutlined />}
                                        aria-label={`Ayuda sobre el acceso del nuevo cliente a ${label}`}
                                      />
                                    </Tooltip>
                                  </Space>
                                  <Switch
                                    checked={draft.is_binding_from_new_to_existing}
                                    onChange={(checked) => {
                                      setRelationshipResolutions((prev) => ({
                                        ...prev,
                                        [match.id]: {
                                          ...(prev[match.id] ?? draft),
                                          existing_user_id: match.id,
                                          is_binding_from_new_to_existing: checked,
                                        },
                                      }));
                                    }}
                                  />
                                </Space>
                                <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                                  <Space size={6}>
                                    <Typography.Text>{label} tendrá acceso</Typography.Text>
                                    <Tooltip title={RELATIONSHIP_ACCESS_HELP.existingToNew}>
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<InfoCircleOutlined />}
                                        aria-label={`Ayuda sobre el acceso de ${label} al nuevo cliente`}
                                      />
                                    </Tooltip>
                                  </Space>
                                  <Switch
                                    checked={draft.is_binding_from_existing_to_new}
                                    onChange={(checked) => {
                                      setRelationshipResolutions((prev) => ({
                                        ...prev,
                                        [match.id]: {
                                          ...(prev[match.id] ?? draft),
                                          existing_user_id: match.id,
                                          is_binding_from_existing_to_new: checked,
                                        },
                                      }));
                                    }}
                                  />
                                </Space>
                              </Space>
                            </div>
                          );
                        })}
                      </Space>
                  )}
                </Space>
              ),
            },
            {
              key: 'advanced',
              label: (
                <Space>
                  <span>Avanzado</span>
                  {extendedErrors && Object.keys(extendedErrors).length > 0 && (
                    <Tag color="error" style={{ margin: 0 }}>
                      Con errores
                    </Tag>
                  )}
                </Space>
              ),
              children: (
                <CustomerExtendedForm
                  value={toExtendedDraft(newCustomer)}     // ← adaptador de entrada
                  errors={extendedErrors}
                  onChange={setExtendedFromForm}           // ← adaptador de salida
                  defaultPhoneCountry={DEFAULT_CUSTOMER_COUNTRY}
                />
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        open={Boolean(pendingPhoneConflictChoice)}
        onCancel={() => setPendingPhoneConflictChoice(null)}
        footer={null}
        title="Clientes asociados"
        width={520}
        destroyOnHidden
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text>
            {'Ya existen clientes asociados con ese n\u00FAmero. \u00BFDeseas usar uno de ellos o asociar uno nuevo?'}
          </Typography.Text>

          <div
            style={{
              border: '1px solid var(--ant-color-border)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            {pendingPhoneConflictChoice?.matches.map((match, index) => {
              const label = [match.first_name, match.last_name].filter(Boolean).join(' ') || `Cliente ${match.id}`;
              const details = match.phone || match.email || `ID ${match.id}`;
              return (
                <div
                  key={match.id}
                  style={{
                    padding: '12px 14px',
                    borderTop: index === 0 ? 'none' : '1px solid var(--ant-color-border-secondary, rgba(5, 5, 5, 0.06))',
                  }}
                >
                  <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <div>
                      <Typography.Text strong style={{ display: 'block' }}>
                        {label}
                      </Typography.Text>
                      <Typography.Text type="secondary">{details}</Typography.Text>
                    </div>
                    <Button onClick={() => void handleUseExistingConflictMatch(match.id)}>
                      Usar este cliente
                    </Button>
                  </Space>
                </div>
              );
            })}
          </div>

          <Button type="primary" block onClick={handleAssociateNewFromConflict}>
            Asociar nuevo
          </Button>
        </Space>
      </Modal>
    </div>
  );
};
